/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { useLocation, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import Pagination from "../../components/Pagination";
import MainBody from "../../components/MainBody/MainBody";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import AddEditContract from "./AddEditContract";
import { LucidePlusCircle } from "lucide-react";
import ContractActionButton from "./ContractActionButton";
import CommonFilters from "../../components/CommonFilters";
import {
  formatNumber,
  formatSubcontractorName,
  getModuleNamingBySlug,
} from "../../lib/Misc";
import { useTrust } from "../../lib/context/TrustContext";
import { useOrganization } from "../../lib/context/OrganisationContext";
import Confirmation from "../../components/Confirmation";
import { cn } from "../../lib/utils";

// Here id => VenderId
const Contracts = ({ id = "", expandList = false }) => {
  const navigate = useNavigate();
  const { trustId } = useTrust();
  const location = useLocation();
  const { organizationData } = useOrganization();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [contractLoading, setContractLoading] = useState(false);
  const [contractList, setContractList] = useState([]);
  const [page, setPage] = useState(expandList ? 1 : defaultPage || 1);
  const [limit, setLimit] = useState(
    expandList ? defaultLimitPagination : defaultLimit || defaultLimitPagination
  );
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();

  const [searchParam, setSearchParam] = useState(
    expandList ? "" : search || ""
  );
  const [searchParamData, setSearchParamData] = useState(
    expandList ? "" : search || ""
  );
  const [contractId, setContractId] = useState(null);

  const [edit, setEdit] = useState(false);
  const [showAddEditContract, setShowAddEditContract] = React.useState(false);

  const [vendorId, setVendorId] = useState();
  const [assetId, setAssetId] = useState();
  const [contactId, setContactId] = useState();
  const [subAssetId, setSubAssetId] = useState();
  const [showContractDelete, setShowContractDelete] = useState(false);

  const toggleShowAddEditContract = () => {
    if (showAddEditContract) {
      setEdit(false);
      setContractId(null);
    }
    setShowAddEditContract(!showAddEditContract);
  };

  const toggleShowDelete = () => {
    if (showContractDelete) {
      setContractId(null);
    }
    setShowContractDelete(!showContractDelete);
  };

  var today = new Date();
  today.setHours(0, 0, 0, 0);

  // *API Call for Geting Contracts - Start
  const fetchContracts = useCallback(
    async (passLimit, passPage) => {
      try {
        setContractLoading(true);
        const { data } = await get(
          `${API.GET_CONTRACTS}?trust_id=${trustId ? trustId : ""}&vendor_id=${
            vendorId ? vendorId : ""
          }&asset_id=${assetId ? assetId : ""}&limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${
            passPage ? passPage : page ? page : 1
          }&document_type=1&search=${
            searchParam ? searchParam : ""
          }&vendor_id=${vendorId ? vendorId : ""}&contact_id=${
            contactId ? contactId : ""
          }&asset_id=${assetId ? assetId : ""}&sub_asset_id=${
            subAssetId ? subAssetId : ""
          }`
        );
        setContractLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const contractList = data.data.data;
        setContractList(contractList);
        return data.data;
      } catch (error) {
        setContractLoading(false);
        setContractList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      id,
      searchParam,
      sortOrder,
      sortField,
      vendorId,
      assetId,
      contactId,
      subAssetId,
      trustId,
    ]
  );

  // *API Call for Geting Contracts - End

  // *Contracts Table Row and Column Generation, Filtering and Sorting of Record - Start
  const contractGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: contractList[i]?.id,
        vendor: contractList[i]?.vendor
          ? formatSubcontractorName(contractList[i]?.vendor)
          : "-",
        asset: contractList[i]?.asset ? contractList[i]?.asset?.name : "-",
        sub_asset: contractList[i]?.sub_asset
          ? contractList[i]?.sub_asset?.name
          : "-",
        contract_number: contractList[i]?.contract_number
          ? contractList[i]?.contract_number
          : "-",
        contract_value: contractList[i]?.contract_value
          ? formatNumber(Number(contractList[i]?.contract_value))
          : "-",
        contract_value_exc: contractList[i]?.contract_value_exc
          ? formatNumber(Number(contractList[i]?.contract_value_exc))
          : "-",

        contract_file: contractList[i]?.contract_file
          ? contractList[i]?.contract_file
          : "-",

        start_date: contractList[i]?.start_date
          ? contractList[i]?.start_date
          : "-",
        end_date: contractList[i]?.end_date ? contractList[i]?.end_date : "-",

        action: (
          <ContractActionButton
            id={contractList[i]?.id}
            used={contractList[i]?.is_used ? true : false}
            toggleShowDelete={toggleShowDelete}
            setEdit={setEdit}
            setContractId={setContractId}
            toggleShowAddEditAsset={toggleShowAddEditContract}
            contract_file={
              contractList[i]?.contract_file
                ? contractList[i]?.contract_file
                : ""
            }
          />
        ),
      });
    }
    return items;
  };

  const contract = contractGenerator(contractList?.length);

  const ContractColumns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "contract_number",
      text: "Agreement / Contract Number",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "contract_value",
      text: "Agreement / Contract Value",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "contract_value_exc",
      text: "Agreement / Contract Value (Exc. GST)",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "start_date",
      text: "Start Date",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "end_date",
      text: "End Date",
    },

    {
      headerClasses: cn(
        THClasses,
        "whitespace-normal break-words" // fixed width + wrapping
      ),
      classes: cn(
        TDClasses,
        "whitespace-normal break-words" // fixed width + wrapping
      ),
      dataField: "vendor",
      text: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "vendors",
        "Vendor"
      )}`,
    },

    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "asset",
      text: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "assets",
        "Asset"
      )}`,
    },

    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "sub_asset",
      text: `${getModuleNamingBySlug(
        organizationData?.organizationData,
        "sub-assets",
        "Sub Asset"
      )}`,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchContracts(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchContracts(limit, page);
  };
  // *Contracts Table Row and Column Generation, Filtering and Sorting of Record - End

  useEffect(() => {
    if (!expandList) {
      navigate(
        `?limit=${limit ? limit : defaultLimitPagination}&page=${
          page ? page : 1
        }&search=${searchParam ? searchParam : ""}&sort_column=${
          sortField ? sortField : ""
        }&sort_order=${sortOrder ? sortOrder : ""}`,
        {
          replace: true,
        }
      );
    }
  }, [navigate, limit, searchParam, page, sortField, sortOrder, expandList]);

  // Single Contract Delete
  const handleContractDelete = async () => {
    try {
      setContractLoading(true);
      await deleteCall(`${API.CONTRACT_DELETE}/${contractId}`);
      setContractLoading(false);
      fetchContracts(limit, page);
    } catch (error) {
      setContractLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        expandList={expandList}
        mainTitle={"Agreements/Contracts"}
        actionContent={
          <>
            {!expandList && (
              <CommonFilters
                trustId={trustId}
                contact={false}
                setContactId={setContactId}
                contactId={contactId}
                vendor={true}
                setVendorId={setVendorId}
                vendorId={vendorId}
                asset={true}
                setAssetId={setAssetId}
                assetId={assetId}
                subAsset={true}
                setSubAssetId={setSubAssetId}
                subAssetId={subAssetId}
              />
            )}

            <ResponsiveDrawerForm
              description={""}
              title={
                edit ? "Edit Agreement/Contract" : "Add Agreement/Contract"
              }
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setShowAddEditContract(false);
              }}
              write={true}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Agreement/Contract</span>
                </div>
              }
              className={""}
              open={showAddEditContract}
              buttonClassName={""}
              setOpen={setShowAddEditContract}
              mobileCancelHidden={true}
            >
              <AddEditContract
                edit={edit}
                toggleShowAddEditContract={toggleShowAddEditContract}
                contractID={contractId}
                fetchContracts={fetchContracts}
                trustId={trustId}
              />
            </ResponsiveDrawerForm>
          </>
        }
        mainClassName="w-[calc(100%-3rem)]"
        loading={contractLoading}
      >
        <div className="custom-table">
          <div className={"table-body-class"}>
            <BootstrapTable
              keyField="contract_id"
              remote
              data={contract}
              columns={ContractColumns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={`Delete Contract`}
        open={showContractDelete}
        setOpen={setShowContractDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={`Are you sure you want to delete contract?`}
        okAction={async () => {
          handleContractDelete();
          toggleShowDelete();
          // fetchContracts(limit, page);
        }}
        cancelAction={() => {
          setContractId(null);
          setShowContractDelete(!showContractDelete);
        }}
      />
    </>
  );
};
export default Contracts;
